import axios from 'axios';
import {FC, FocusEvent, useState, useEffect, useRef} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import styled from 'styled-components';
import useForm from '../../hooks/useForm';
import {fadeIn} from '../../styles/animations';
import {API_ENDPOINT} from '../../utils/constants';
import {APIError, FetchResponse, FormFields} from '../../utils/types';
import Button from '../components/Button';
import FormStatus from '../components/FormStatus';
import Input2 from "../components/Input2";

// Custom OTP Input Component
const CustomOtpInput = ({ value, onChange, numInputs = 4 }: any) => {
    const [otp, setOtp] = useState(value || "");
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

    useEffect(() => {
        setOtp(value || "");
    }, [value]);

    const handleChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
        const digit = e.target.value.replace(/\D/g, '');
        
        if (digit.length > 1) {
            const digits = digit.slice(0, numInputs - index).split('');
            const newOtp = [...otp.split("")];
            digits.forEach((d, i) => {
                if (index + i < numInputs) {
                    newOtp[index + i] = d;
                }
            });
            const newOtpString = newOtp.join("").slice(0, numInputs);
            setOtp(newOtpString);
            onChange(newOtpString);
            
            const nextIndex = Math.min(index + digits.length, numInputs - 1);
            inputRefs.current[nextIndex]?.focus();
            return;
        }
        
        if (digit.length === 0) {
            const newOtp = otp.split("");
            newOtp[index] = "";
            const newOtpString = newOtp.join("");
            setOtp(newOtpString);
            onChange(newOtpString);
            return;
        }
        
        const newOtp = otp.split("");
        newOtp[index] = digit;
        const newOtpString = newOtp.join("");
        setOtp(newOtpString);
        onChange(newOtpString);
        
        if (index < numInputs - 1 && digit) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace") {
            if (!otp[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
                const newOtp = otp.split("");
                newOtp[index - 1] = "";
                const newOtpString = newOtp.join("");
                setOtp(newOtpString);
                onChange(newOtpString);
            } else if (otp[index]) {
                const newOtp = otp.split("");
                newOtp[index] = "";
                const newOtpString = newOtp.join("");
                setOtp(newOtpString);
                onChange(newOtpString);
            }
        } else if (e.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === "ArrowRight" && index < numInputs - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text/plain").replace(/\D/g, '').slice(0, numInputs);
        if (pastedData.length > 0) {
            const newOtp = pastedData.padEnd(numInputs, "").split("").slice(0, numInputs);
            const newOtpString = newOtp.join("");
            setOtp(newOtpString);
            onChange(newOtpString);
            
            const focusIndex = Math.min(pastedData.length - 1, numInputs - 1);
            inputRefs.current[focusIndex]?.focus();
        }
    };

    return (
        <OtpContainer>
            {Array.from({ length: numInputs }, (_, index) => (
                <OtpInputField
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={otp[index] || ""}
                    onChange={(e) => handleChange(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    autoFocus={index === 0 && !otp}
                />
            ))}
        </OtpContainer>
    );
};

type Step = 'email' | 'otp' | 'password';

const ForgetPasswordPage: FC = () => {
    const history = useHistory();
    const location = useLocation<any>();
    const [step, setStep] = useState<Step>('email');
    const [email, setEmail] = useState<string>('');
    const [verificationToken, setVerificationToken] = useState<string>('');
    const [isLoading, setLoading] = useState(false);
    const [otp, setOtp] = useState<string>('');
    const [otpError, setOtpError] = useState<string>('');
    const [otpSent, setOtpSent] = useState<boolean>(false);
    const [resendDelay, setResendDelay] = useState<number>(0);
    const timerRef = useRef<NodeJS.Timeout | null>(null);

    // Get redirect URL from query params or location state
    const urlSearchParams = new URLSearchParams(location.search);
    const redirectUrl = urlSearchParams.get('redirect_to') || location.state?.redirectUrl || null;

    // Step 1: Email form
    const actionOnSubmitEmail = async (
        data: { [key in FormFields]?: string }
    ): Promise<APIError[] | string> => {
        setLoading(true);
        try {
            let url = API_ENDPOINT + '/forget-password/request-otp';
            if (redirectUrl) {
                url += `?redirect_to=${encodeURIComponent(redirectUrl)}`;
            }

            const {
                data: {
                    data: {message},
                },
            } = await axios.post<FetchResponse<{ message: string }>>(url, {
                email: data.email,
            });

            setEmail(data.email || '');
            setStep('otp');
            setLoading(false);
            return message;
        } catch (error: any) {
            setLoading(false);
            const errors = error.response?.data?.error as APIError[];
            return errors || [{email: 'Failed to send OTP. Please try again.'}];
        }
    };

    const {
        formData: emailFormData,
        formSubmitResponse: emailFormResponse,
        handleChange: handleEmailChange,
        handleBlur: handleEmailBlur,
        handleSubmit: handleEmailSubmit,
    } = useForm({
        actions: {onSubmit: actionOnSubmitEmail},
        validatorSchema: {
            email: {
                name: 'Email address',
                required: true,
                regex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                errorText: 'Please enter a valid email',
            },
        },
        handleValidationOnBlur: true,
    });

    // Handle OTP resend timer
    useEffect(() => {
        if (resendDelay > 0) {
            timerRef.current = setInterval(() => {
                setResendDelay((prev) => {
                    if (prev <= 1) {
                        if (timerRef.current) {
                            clearInterval(timerRef.current);
                            timerRef.current = null;
                        }
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
    }, [resendDelay]);

    // Step 2: OTP verification
    const handleVerifyOtp = async () => {
        if (otp.length !== 4) {
            setOtpError('Please enter a complete 4-digit OTP code.');
            return;
        }

        if (!/^\d{4}$/.test(otp)) {
            setOtpError('OTP should only contain numbers. Please enter a valid 4-digit code.');
            return;
        }

        setLoading(true);
        setOtpError('');
        
        try {
            const resp = await axios.post<FetchResponse<{ verification_token: string }>>(
                API_ENDPOINT + '/forget-password/verify-otp',
                {
                    email: email,
                    otp: parseInt(otp),
                }
            );
            
            if (resp.data?.data?.verification_token) {
                setVerificationToken(resp.data.data.verification_token);
                setStep('password');
            } else {
                setOtpError('Verification failed. Please try again.');
            }
        } catch (error: any) {
            let errorMessage = 'Unable to verify OTP. Please try again.';
            
            if (error.response?.status === 400) {
                errorMessage = error.response?.data?.message || 'The OTP you entered is incorrect or has expired.';
            } else if (error.response?.data?.error?.[0]?.otp) {
                errorMessage = error.response.data.error[0].otp;
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            setOtpError(errorMessage);
            setOtp('');
        }
        
        setLoading(false);
    };

    const handleResendOtp = async () => {
        if (resendDelay > 0) return;
        
        setLoading(true);
        setOtpError('');
        setOtp('');
        
        try {
            let url = API_ENDPOINT + '/forget-password/request-otp';
            if (redirectUrl) {
                url += `?redirect_to=${encodeURIComponent(redirectUrl)}`;
            }

            await axios.post<FetchResponse<{ message: string }>>(url, {
                email: email,
            });
            
            setResendDelay(60);
            setOtpSent(true);
        } catch (error: any) {
            const errors = error.response?.data?.error as APIError[];
            setOtpError(errors?.[0]?.email || 'Failed to resend OTP. Please try again.');
        }
        
        setLoading(false);
    };

    // Auto-send OTP when step changes to 'otp'
    useEffect(() => {
        if (step === 'otp' && email && !otpSent) {
            setOtpSent(true);
            setResendDelay(60);
        }
    }, [step, email, otpSent]);

    // Step 3: Password reset form
    const actionOnSubmitPassword = async (data: any): Promise<APIError[] | string> => {
        setLoading(true);
        try {
            delete data['confirm_password'];

            const {
                data: {
                    data: {message},
                },
            } = await axios.patch<FetchResponse<{ message: string }>>(
                API_ENDPOINT + '/forget-password/reset',
                {
                    verification_token: verificationToken,
                    new_password: data.new_password,
                }
            );

            setLoading(false);
            
            // Redirect back to original URL or login page
            if (redirectUrl) {
                try {
                    const redirectTo = new URL(decodeURIComponent(redirectUrl));
                    window.location.href = redirectTo.href;
                } catch (e) {
                    history.push({
                        pathname: '/login',
                        state: { 
                            response: true, 
                            message: 'Password reset successfully. Please login with your new password.' 
                        },
                    });
                }
            } else {
                history.push({
                    pathname: '/login',
                    state: { 
                        response: true, 
                        message: 'Password reset successfully. Please login with your new password.' 
                    },
                });
            }
            
            return message;
        } catch (error: any) {
            setLoading(false);
            const errors = error.response?.data?.error as APIError[];
            return errors || [{new_password: 'Failed to reset password. Please try again.'}];
        }
    };

    const {
        formData: passwordFormData,
        setFormData: setPasswordFormData,
        formSubmitResponse: passwordFormResponse,
        handleChange: handlePasswordChange,
        handleBlur: handlePasswordBlur,
        handleSubmit: handlePasswordSubmit,
    } = useForm({
        actions: {onSubmit: actionOnSubmitPassword},
        validatorSchema: {
            new_password: {
                name: 'New Password',
                required: true,
            },
            confirm_password: {
                name: 'Confirm Password',
                required: true,
            },
        },
        handleValidationOnBlur: true,
    });

    const handleConfirmPasswordBlur = (e: FocusEvent<HTMLInputElement>) => {
        const key = e.target.name as FormFields;
        const value = e.target.value;

        if (value !== passwordFormData.new_password?.value) {
            setPasswordFormData({
                ...passwordFormData,
                [key]: {
                    ...passwordFormData[key],
                    error: "Passwords don't match",
                },
            });
        }
    };

    const isEmailButtonDisabled = () => {
        return !(emailFormData.email?.value !== '' && emailFormData.email?.error === '');
    };

    const isPasswordButtonDisabled = () => {
        return !(
            passwordFormData.new_password?.value !== '' &&
            passwordFormData.new_password?.error === '' &&
            passwordFormData.confirm_password?.value !== '' &&
            passwordFormData.confirm_password?.error === ''
        );
    };

    return (
        <>
            <Container>
                <RightBanner>
                {step === 'email' && (
                    <>
                        <Heading>Forgot Password</Heading>
                        <Text>
                            Please enter your registered email address. We'll send you an OTP to reset your password.
                        </Text>
                        <Separator/>
                        <form onSubmit={handleEmailSubmit}>
                            <StyleInput
                                label="Email address"
                                name="email"
                                type="email"
                                placeholder="Enter email address"
                                value={emailFormData.email?.value}
                                error={emailFormData.email?.error}
                                required
                                autoFocus
                                onChange={handleEmailChange}
                                onBlur={handleEmailBlur}
                            />
                            <StyledButton
                                type="submit"
                                isLoading={isLoading}
                                disabled={isEmailButtonDisabled()}
                            >
                                Send OTP
                            </StyledButton>
                        </form>
                        {emailFormResponse && (
                            <FormStatus
                                align="center"
                                className={emailFormResponse.status}
                                style={{marginTop: '2rem'}}
                            >
                                {emailFormResponse.message}
                            </FormStatus>
                        )}
                    </>
                )}

                {step === 'otp' && email && (
                    <>
                        <Heading>Verify OTP</Heading>
                        <Text>
                            We've sent a 4-digit OTP to <strong>{email}</strong>. Please enter it below to continue.
                        </Text>
                        <Separator/>
                        <div style={{ marginTop: '2rem' }}>
                            <CustomOtpInput value={otp} onChange={setOtp} numInputs={4} />
                            {otpError && (
                                <FormStatus
                                    align="center"
                                    className="error"
                                    style={{ marginTop: '1rem' }}
                                >
                                    {otpError}
                                </FormStatus>
                            )}
                            <StyledButton
                                type="button"
                                onClick={handleVerifyOtp}
                                isLoading={isLoading}
                                disabled={otp.length !== 4 || isLoading}
                                style={{ marginTop: '2rem' }}
                            >
                                Verify OTP
                            </StyledButton>
                            <ResendContainer>
                                {resendDelay > 0 ? (
                                    <ResendText>
                                        Resend OTP in {Math.floor(resendDelay / 60)}:{(resendDelay % 60).toString().padStart(2, '0')}
                                    </ResendText>
                                ) : (
                                    <ResendLink onClick={handleResendOtp} disabled={isLoading}>
                                        Resend OTP
                                    </ResendLink>
                                )}
                            </ResendContainer>
                        </div>
                    </>
                )}

                {step === 'password' && (
                    <>
                        <Heading>Create New Password</Heading>
                        <Text>Enter your new password below.</Text>
                        <Separator/>
                        <form onSubmit={handlePasswordSubmit}>
                            <StyleInput
                                label="New Password"
                                name="new_password"
                                type="password"
                                placeholder="Enter new password"
                                value={passwordFormData.new_password?.value}
                                error={passwordFormData.new_password?.error}
                                required
                                autoFocus
                                onChange={handlePasswordChange}
                                onBlur={handlePasswordBlur}
                            />
                            <StyleInput
                                label="Confirm Password"
                                name="confirm_password"
                                type="password"
                                placeholder="Enter confirm password"
                                value={passwordFormData.confirm_password?.value}
                                error={passwordFormData.confirm_password?.error}
                                required
                                onChange={handlePasswordChange}
                                onBlur={handleConfirmPasswordBlur}
                            />
                            <StyledButton
                                type="submit"
                                isLoading={isLoading}
                                disabled={isPasswordButtonDisabled()}
                            >
                                Reset Password
                            </StyledButton>
                        </form>

                        {passwordFormResponse && (
                            <FormStatus
                                align="center"
                                className={passwordFormResponse.status}
                                style={{marginTop: '2rem'}}
                            >
                                {passwordFormResponse.message}
                            </FormStatus>
                        )}
                    </>
                )}
                </RightBanner>
            </Container>
            <Foot>
                <Text>© 2026, Stride Ahead™</Text>
            </Foot>
        </>
    );
};

export default ForgetPasswordPage;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: calc(100vh - 60px);
`;

const RightBanner = styled.div`
  padding: 40px;
  max-width: 500px;
  width: 100%;
  margin: 0 auto;
  @media(max-width: 576px){
    padding: 20px;
  } 
`;

const Heading = styled.h1`
  font-weight: 700;
  font-size: 24px;
  line-height: 30px;
  color: #073b5f;
  text-align: center;
  @media(max-width:576px){
    font-size: 20px;
    }
`;

const Text = styled.p`
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: #93acbd;
  margin: 0;
  text-align: center;
  @media(max-width:576px){
    font-size: 12px;
    }
`;

const Separator = styled.div`
  height: 1px;
  background: #e0e0e0;
  margin: 2rem 0;
`;

const StyleInput = styled(Input2)`
  
`;

const StyledButton = styled(Button)`
  background: #3f52fd;
  border-radius: 10px;
  margin-top: 2rem;
  width: 100%;
  @media(max-width:576px){
   margin-top:2rem;
   height:50px !important;
    }
`;

const Foot = styled.div`
  @media(min-width:576px){
   display:none;
   }
   @media(max-width:576px){
    margin-top:20px;
    text-align:center;
    display:block;
   }
   @media (min-width: 768px) and (max-width: 1024px){
    margin-top:40px;
    text-align:center;
    display:block;
  }
`;

const OtpContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 2rem 0;
`;

const OtpInputField = styled.input`
  width: 60px;
  height: 60px;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  outline: none;
  transition: border-color 0.3s;

  &:focus {
    border-color: #3f52fd;
  }

  @media(max-width: 576px) {
    width: 50px;
    height: 50px;
    font-size: 20px;
  }
`;

const ResendContainer = styled.div`
  text-align: center;
  margin-top: 1rem;
`;

const ResendText = styled.p`
  color: #93acbd;
  font-size: 14px;
  margin: 0;
`;

const ResendLink = styled.a<{ disabled?: boolean }>`
  color: #3f52fd;
  font-size: 14px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-decoration: none;
  
  &:hover {
    text-decoration: ${props => props.disabled ? 'none' : 'underline'};
  }
`;
